import { generateHTML } from '@tiptap/core';
import DOMPurify from 'dompurify';
import { getExtensions } from './extensions.js';
import { blankSyntax } from './blanks.js';

export function isDoc(value) {
  return value != null && typeof value === 'object' && value.type === 'doc';
}

export function textToDoc(text = '') {
  const paragraphs = String(text).split(/\n/);
  return {
    type: 'doc',
    content: paragraphs.map((p) => (p ? { type: 'paragraph', content: [{ type: 'text', text: p }] } : { type: 'paragraph' })),
  };
}

const BLOCKS = new Set(['paragraph', 'heading', 'listItem', 'blockquote', 'codeBlock']);

/** Plain text of a field value (doc or string), used for search, cloze parsing and sort keys. */
export function toText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map((v) => toText(v?.text ?? v)).join(' ');
  if (!isDoc(value) && typeof value !== 'object') return String(value);
  const out = [];
  const walk = (node) => {
    if (!node) return;
    if (node.type === 'blank') {
      const written = [];
      const collect = (child) => {
        if (!child) return;
        if (child.type === 'text') written.push(child.text ?? '');
        else if (child.type === 'hardBreak') written.push(' ');
        else child.content?.forEach(collect);
      };
      node.content?.forEach(collect);
      const answer = written.join('') || node.attrs?.answer || '';
      out.push(blankSyntax(node.attrs?.n, answer, node.attrs?.hint ?? ''));
      return;
    }
    if (node.type === 'text') out.push(node.text ?? '');
    if (node.type === 'hardBreak') out.push('\n');
    node.content?.forEach(walk);
    if (BLOCKS.has(node.type)) out.push('\n');
  };
  walk(value);
  return out.join('').replace(/\n+$/, '');
}

export function isEmptyValue(value) {
  return toText(value).trim() === '';
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const htmlCache = new WeakMap();

export function sanitize(html) {
  return DOMPurify.sanitize(html, {
    ADD_ATTR: ['target', 'rel'],
    FORBID_TAGS: ['style', 'script', 'iframe', 'form', 'input'],
  });
}

/** Field value -> sanitized HTML (unsafe input never reaches the DOM). */
export function toHtml(value) {
  if (value == null) return '';
  if (typeof value === 'string') return escapeHtml(value).replace(/\n/g, '<br>');
  if (!isDoc(value)) return escapeHtml(toText(value));
  let html = htmlCache.get(value);
  if (html === undefined) {
    try {
      html = sanitize(generateHTML(value, getExtensions()));
    } catch {
      html = escapeHtml(toText(value)).replace(/\n/g, '<br>');
    }
    htmlCache.set(value, html);
  }
  return html;
}

export { escapeHtml };
